import { classes } from 'html-classes';
import {
  forwardRef,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import styles from './Input.module.scss';

type BaseInputProps = {
  variant?: 'default' | 'rounded' | 'transparent';
  size?: 'S' | 'M';
  error?: boolean;
  label?: string;
  rounded?: boolean;
};

type InputAsInput = BaseInputProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
    as?: 'input';
  };

type InputAsTextarea = BaseInputProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
    as: 'textarea';
    rows?: number;
  };

export type InputProps = InputAsInput | InputAsTextarea;

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (props, ref) => {
    const {
      variant = 'default',
      size,
      error,
      label,
      rounded,
      className,
      as = 'input',
      ...restProps
    } = props;

    const isRounded = variant === 'rounded' || rounded;

    const elementClassName = classes([
      styles.input,
      variant === 'transparent' && styles.transparent,
      isRounded && styles.rounded,
      size === 'S' && styles.sizeS,
      size === 'M' && styles.sizeM,
      error && styles.error,
      as === 'textarea' && styles.textarea,
      className,
    ]);

    const inputElement =
      as === 'textarea' ? (
        <textarea
          {...(restProps as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          ref={ref as React.Ref<HTMLTextAreaElement>}
          className={elementClassName}
          rows={(restProps as any).rows || 3}
        />
      ) : (
        <input
          {...(restProps as InputHTMLAttributes<HTMLInputElement>)}
          ref={ref as React.Ref<HTMLInputElement>}
          className={elementClassName}
        />
      );

    if (label) {
      return (
        <label className={styles.inputWrapper}>
          <span className={styles.label}>{label}</span>
          {inputElement}
        </label>
      );
    }

    return inputElement;
  },
);

Input.displayName = 'Input';

export default Input;
